import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartContainer } from "@/components/charts/ChartContainer";
import { formatCurrency } from "@/utils/format";
import { cn } from "@/utils/cn";
import {
  AGING_ORDEM,
  CORTE_LABEL,
  CORTE_ORDEM,
  formatAnoMes,
  formatTaxaPct,
  type AgingFaixa,
  type CortePeriodo,
} from "../inadimplenciaUtils";
import type {
  InadimplenciaFechamento,
  InadimplenciaPontoMensal,
  InadimplenciaQuebra,
  InadimplenciaValorCorte,
} from "@/services/inadimplencia";

const ROSE = "#fb7185";
const TEAL = "#2dd4bf";
const SLATE = "#e2e8f0";
const AGING_COLORS = ["#fda4af", "#fb7185", "#e11d48", "#9f1239"];

function KpiDonut({ value, color }: { value: number; color: string }) {
  const pct = Math.max(0, Math.min(1, value));
  const data = [
    { name: "ok", value: pct },
    { name: "rest", value: 1 - pct },
  ];
  return (
    <PieChart width={56} height={56}>
      <Pie data={data} dataKey="value" innerRadius={18} outerRadius={26} startAngle={90} endAngle={-270} stroke="none">
        <Cell fill={color} />
        <Cell fill={SLATE} />
      </Pie>
    </PieChart>
  );
}

export function KpiDonutCard({
  title,
  value,
  subtitle,
  color,
  loading,
  accent,
  onClick,
}: {
  title: string;
  value: number;
  subtitle?: string;
  color: string;
  loading?: boolean;
  accent: string;
  onClick?: () => void;
}) {
  return (
    <div
      className={cn(
        "rounded-xl border border-slate-200 bg-white p-4 shadow-card border-l-4",
        accent,
        onClick && "cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md",
      )}
      onClick={onClick}
      role={onClick ? "button" : undefined}
    >
      <p className="text-xs font-semibold text-slate-500">{title}</p>
      <div className="mt-1 flex items-center gap-3">
        {loading ? (
          <p className="text-xl font-bold text-slate-300">—</p>
        ) : (
          <>
            <p className="text-xl font-bold tabular-nums text-slate-900">{formatTaxaPct(value)}</p>
            <KpiDonut value={value} color={color} />
          </>
        )}
      </div>
      {subtitle && <p className="mt-1 text-xs text-slate-500">{subtitle}</p>}
    </div>
  );
}

function corteTooltip(
  label: string,
  qtdTotal: number,
  qtdVencido: number,
  carteira: number,
  vencido: number,
  taxa?: number,
) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs shadow-modal">
      <p className="font-semibold text-slate-800">{label}</p>
      <p className="mt-1 text-slate-600">{qtdVencido} inadimplente(s) de {qtdTotal} vencimento(s)</p>
      {taxa != null && <p className="text-slate-500">{formatTaxaPct(taxa)}</p>}
      <p className="mt-1 text-slate-500">Valor dos vencimentos {formatCurrency(carteira)}</p>
      <p className="text-slate-500">Saldo inadimplente {formatCurrency(vencido)}</p>
    </div>
  );
}

export function CortesValorChart({
  cortes,
  loading,
  ultimoFechamento,
  onSelectCorte,
}: {
  cortes: Record<CortePeriodo, InadimplenciaValorCorte> | undefined;
  loading: boolean;
  ultimoFechamento?: InadimplenciaFechamento | null;
  onSelectCorte?: (corte: CortePeriodo, serie: "carteira" | "vencido") => void;
}) {
  const data = CORTE_ORDEM.map((corte) => ({
    name: CORTE_LABEL[corte],
    carteira: cortes?.[corte]?.carteira ?? 0,
    vencido: cortes?.[corte]?.vencido ?? 0,
    qtdTotal: cortes?.[corte]?.qtdTotal ?? 0,
    qtdVencido: cortes?.[corte]?.qtdVencido ?? 0,
    taxa: cortes?.[corte]?.taxa ?? 0,
  }));
  const fechLabel = ultimoFechamento
    ? `Fechamento ${formatAnoMes(ultimoFechamento.anoMes)} gravado — taxa ${formatTaxaPct(ultimoFechamento.kpis.taxaValor)}`
    : "O fechamento do mês é gravado automaticamente na virada.";

  return (
    <ChartContainer
      title="Inadimplência por vencimento"
      subtitle={`Inadimplentes ÷ parcelas com vencimento no período (inclui pagas; exclui reparceladas). Períodos fixos. ${fechLabel}`}
      loading={loading}
      height={280}
    >
      <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
        <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} allowDecimals={false} />
        <Tooltip
          cursor={{ fill: "rgba(15,23,42,0.04)" }}
          content={({ active, payload }) => {
            if (!active || !payload?.[0]) return null;
            const row = payload[0].payload as (typeof data)[number];
            return corteTooltip(row.name, row.qtdTotal, row.qtdVencido, row.carteira, row.vencido, row.taxa);
          }}
        />
        <Bar
          dataKey="qtdTotal"
          name="Vencimentos"
          fill={TEAL}
          radius={[4, 4, 0, 0]}
          maxBarSize={28}
          cursor={onSelectCorte ? "pointer" : undefined}
          onClick={(_d, index) => onSelectCorte?.(CORTE_ORDEM[index], "carteira")}
        />
        <Bar
          dataKey="qtdVencido"
          name="Inadimplentes"
          fill={ROSE}
          radius={[4, 4, 0, 0]}
          maxBarSize={28}
          cursor={onSelectCorte ? "pointer" : undefined}
          onClick={(_d, index) => onSelectCorte?.(CORTE_ORDEM[index], "vencido")}
        />
      </BarChart>
    </ChartContainer>
  );
}

export function AgingBarChart({
  aging,
  loading,
  selected,
  onSelect,
}: {
  aging: Record<AgingFaixa, number> | undefined;
  loading: boolean;
  selected: AgingFaixa | "todas" | "spc30";
  onSelect: (faixa: AgingFaixa) => void;
}) {
  const data = AGING_ORDEM.map((faixa, i) => ({
    name: `${faixa} dias`,
    faixa,
    valor: aging?.[faixa] ?? 0,
    fill: AGING_COLORS[i],
  }));

  return (
    <ChartContainer title="Aging do atraso" loading={loading} height={280}>
      <BarChart data={data} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
        <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} tickFormatter={(v) => formatCurrency(Number(v), true)} />
        <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} width={72} />
        <Tooltip
          cursor={{ fill: "rgba(15,23,42,0.04)" }}
          content={({ active, payload }) => {
            if (!active || !payload?.[0]) return null;
            const row = payload[0].payload as (typeof data)[number];
            return (
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs shadow-modal">
                <p className="font-semibold text-slate-800">{row.name}</p>
                <p className="mt-1 text-slate-600">{formatCurrency(row.valor)}</p>
              </div>
            );
          }}
        />
        <Bar
          dataKey="valor"
          radius={[0, 4, 4, 0]}
          maxBarSize={18}
          cursor="pointer"
          onClick={(_data, index) => {
            const row = data[index];
            if (row) onSelect(row.faixa);
          }}
        >
          {data.map((row) => (
            <Cell key={row.faixa} fill={row.fill} opacity={selected === row.faixa || selected === "todas" || selected === "spc30" ? 1 : 0.35} />
          ))}
        </Bar>
      </BarChart>
    </ChartContainer>
  );
}

export function EmpreendimentoRanking({
  rows,
  loading,
  selectedKey,
  onSelect,
}: {
  rows: InadimplenciaQuebra[];
  loading: boolean;
  selectedKey: string;
  onSelect: (key: string, label: string) => void;
}) {
  const max = Math.max(1, ...rows.map((r) => r.qtdVencido ?? r.vencido));
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-card">
      <h3 className="text-sm font-semibold text-slate-800">Empreendimento</h3>
      <p className="mt-0.5 text-xs text-slate-500">Inadimplentes ÷ vencimentos no período selecionado</p>
      <div className="mt-3 max-h-[280px] overflow-y-auto">
        {loading && <p className="py-6 text-center text-xs text-slate-500">Carregando…</p>}
        {!loading && rows.length === 0 && (
          <p className="py-6 text-center text-xs text-slate-500">Sem dados neste corte.</p>
        )}
        {rows.map((row) => (
          <button
            key={row.key}
            type="button"
            onClick={() => onSelect(row.key, row.label)}
            className={cn(
              "flex w-full flex-col gap-1 rounded-lg px-2 py-2 text-left text-xs hover:bg-slate-50",
              selectedKey === row.key && "bg-brand-50",
            )}
          >
            <div className="flex items-center justify-between gap-3">
              <span className="min-w-0 truncate font-medium text-slate-800">{row.label}</span>
              <span className="shrink-0 tabular-nums text-slate-600">
                {formatTaxaPct(row.taxaValor)} · {row.qtdVencido ?? 0}/{row.qtdTotal ?? 0}
              </span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-rose-400" style={{ width: `${Math.round(((row.qtdVencido ?? 0) / max) * 100)}%` }} />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export function EmpreendimentoTaxaChart({
  rows,
  loading,
  selectedKey,
  onSelect,
}: {
  rows: InadimplenciaQuebra[];
  loading: boolean;
  selectedKey?: string;
  onSelect?: (key: string, label: string) => void;
}) {
  const data = rows.slice(0, 12).map((row) => ({
    ...row,
    name: row.label,
    taxaPct: (row.taxaValor ?? 0) * 100,
  }));
  const height = Math.max(280, 48 + data.length * 28);

  return (
    <ChartContainer
      title="Inadimplência por empreendimento"
      subtitle="Taxa no período selecionado (inadimplentes ÷ vencimentos, inclui pagas; exclui reparceladas)"
      loading={loading}
      height={height}
    >
      <BarChart data={data} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
        <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${Number(v).toFixed(0)}%`} />
        <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} width={168} />
        <Tooltip
          cursor={{ fill: "rgba(15,23,42,0.04)" }}
          content={({ active, payload }) => {
            if (!active || !payload?.[0]) return null;
            const row = payload[0].payload as (typeof data)[number];
            return corteTooltip(row.label, row.qtdTotal ?? 0, row.qtdVencido ?? 0, row.carteira, row.vencido, row.taxaValor);
          }}
        />
        <Bar
          dataKey="taxaPct"
          name="Taxa"
          radius={[0, 4, 4, 0]}
          maxBarSize={16}
          cursor={onSelect ? "pointer" : undefined}
          onClick={(_d, index) => {
            const row = data[index];
            if (row) onSelect?.(row.key, row.label);
          }}
        >
          {data.map((row) => (
            <Cell key={row.key} fill={ROSE} opacity={!selectedKey || selectedKey === row.key ? 1 : 0.35} />
          ))}
        </Bar>
      </BarChart>
    </ChartContainer>
  );
}

export function EmpreendimentoVolumeChart({
  rows,
  loading,
  selectedKey,
  onSelect,
}: {
  rows: InadimplenciaQuebra[];
  loading: boolean;
  selectedKey?: string;
  onSelect?: (key: string, label: string) => void;
}) {
  const data = rows.slice(0, 12).map((row) => ({
    ...row,
    name: row.label.length > 18 ? `${row.label.slice(0, 16)}…` : row.label,
  }));

  return (
    <ChartContainer
      title="Vencimentos por empreendimento"
      subtitle="Quantidade de parcelas com vencimento no período × inadimplentes"
      loading={loading}
      height={280}
    >
      <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 24 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
        <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} interval={0} angle={-20} textAnchor="end" height={48} />
        <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} allowDecimals={false} />
        <Tooltip
          cursor={{ fill: "rgba(15,23,42,0.04)" }}
          content={({ active, payload }) => {
            if (!active || !payload?.[0]) return null;
            const row = payload[0].payload as (typeof data)[number];
            return corteTooltip(row.label, row.qtdTotal ?? 0, row.qtdVencido ?? 0, row.carteira, row.vencido, row.taxaValor);
          }}
        />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar
          dataKey="qtdTotal"
          name="Vencimentos"
          fill={TEAL}
          radius={[4, 4, 0, 0]}
          maxBarSize={22}
          cursor={onSelect ? "pointer" : undefined}
          onClick={(_d, index) => {
            const row = data[index];
            if (row) onSelect?.(row.key, row.label);
          }}
        >
          {data.map((row) => (
            <Cell key={`t-${row.key}`} fill={TEAL} opacity={!selectedKey || selectedKey === row.key ? 1 : 0.35} />
          ))}
        </Bar>
        <Bar
          dataKey="qtdVencido"
          name="Inadimplentes"
          fill={ROSE}
          radius={[4, 4, 0, 0]}
          maxBarSize={22}
          cursor={onSelect ? "pointer" : undefined}
          onClick={(_d, index) => {
            const row = data[index];
            if (row) onSelect?.(row.key, row.label);
          }}
        >
          {data.map((row) => (
            <Cell key={`v-${row.key}`} fill={ROSE} opacity={!selectedKey || selectedKey === row.key ? 1 : 0.35} />
          ))}
        </Bar>
      </BarChart>
    </ChartContainer>
  );
}

export function EvolucaoMensalChart({
  serie,
  loading,
  onSelectMes,
}: {
  serie: InadimplenciaPontoMensal[];
  loading: boolean;
  onSelectMes?: (anoMes: string) => void;
}) {
  const data = serie.map((row) => ({
    ...row,
    name: formatAnoMes(row.anoMes),
    taxaPct: (row.taxa ?? 0) * 100,
  }));

  return (
    <ChartContainer
      title="Evolução mensal da inadimplência"
      subtitle="Como cada mês de vencimento fechou: inadimplentes ÷ parcelas que venceram naquele mês"
      loading={loading}
      height={300}
    >
      <ComposedChart data={data} margin={{ top: 8, right: 36, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
        <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
        <YAxis yAxisId="qtd" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} allowDecimals={false} />
        <YAxis yAxisId="taxa" orientation="right" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${Number(v).toFixed(0)}%`} />
        <Tooltip
          cursor={{ fill: "rgba(15,23,42,0.04)" }}
          content={({ active, payload }) => {
            if (!active || !payload?.[0]) return null;
            const row = payload[0].payload as (typeof data)[number];
            return corteTooltip(row.name, row.qtdTotal, row.qtdVencido, row.carteira, row.vencido, row.taxa);
          }}
        />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar
          yAxisId="qtd"
          dataKey="qtdTotal"
          name="Vencimentos"
          fill={TEAL}
          radius={[4, 4, 0, 0]}
          maxBarSize={22}
          cursor={onSelectMes ? "pointer" : undefined}
          onClick={(_d, index) => {
            const row = data[index];
            if (row) onSelectMes?.(row.anoMes);
          }}
        />
        <Bar
          yAxisId="qtd"
          dataKey="qtdVencido"
          name="Inadimplentes"
          fill={ROSE}
          radius={[4, 4, 0, 0]}
          maxBarSize={22}
          cursor={onSelectMes ? "pointer" : undefined}
          onClick={(_d, index) => {
            const row = data[index];
            if (row) onSelectMes?.(row.anoMes);
          }}
        />
        <Line
          yAxisId="taxa"
          type="monotone"
          dataKey="taxaPct"
          name="Taxa %"
          stroke="#9f1239"
          strokeWidth={2}
          dot={{ r: 3, fill: "#9f1239" }}
        />
      </ComposedChart>
    </ChartContainer>
  );
}
